/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { Loader, Table } from '../../../../../components';
import { Active, Checkbox, Edit, Groups, Tags, YearGroup } from './StudentsTable.components';
import { Continue } from '../../../../../components/selection-action/SelectionActionControl';
import { createGlobalStyle } from 'styled-components';
import type { Column } from 'react-table';
import type { RosterManagementStudentListItem } from '@whizz/api-model';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_active'],
    [data-column='cell_active'] {
      > div {
        display: flex;
        justify-content: center;
      }
    }
    [data-id='col_action'] {
      width: 0;
    }
    [data-id='col_name'] {
      width: 30%;
    }
    [data-id='col_active'],
    [data-id='col_year_group'] {
      width: 10%;
    }
    [data-id='col_classes'] {
      width: 30%;
      max-width: 30%;
      overflow-x: auto;
    }
  }
`;

interface Props {
  students: RosterManagementStudentListItem[],
  baseStudents: RosterManagementStudentListItem[],
  //history: LessonHistory[];
  onEdit: (student: RosterManagementStudentListItem) => void;
  onContinueWithSelected: (students: RosterManagementStudentListItem[]) => void;
  isLoading: boolean;
  action: RosterManagementStudentActionEnum | null;
  isContinueLoading?: boolean;
  showContinue: boolean
  setShowContinue: React.Dispatch<React.SetStateAction<boolean>>
  customHeaderComponent?: JSX.Element | null;
}

const StudentsTable = (
  {
    students,
    baseStudents,
    onEdit,
    onContinueWithSelected,
    isLoading,
    action,
    isContinueLoading = false,
    showContinue,
    setShowContinue,
    customHeaderComponent,
  }: Props,
): JSX.Element => {
  const { translate } = useI18n();

  const [ selectedStudents, setSelectedStudents ] = useState<number[]>([]);

  const [
    maxNumberOfClasses,
    maxNumberOfTags,
  ] = students
    .reduce((acc, student) => ([
      Math.max(acc[0], student.classes.length),
      Math.max(acc[1], student.tags.length),
    ]), [ 1, 1 ]);

  const toggleSelectedStudent = (studentId: number): void => {
    setShowContinue(true);
    if (!selectedStudents.includes(studentId)) {
      setSelectedStudents((prev) => [ ...prev, studentId ]);

      return;
    }

    setSelectedStudents((prev) => [ ...prev ].filter((id) => studentId !== id));
  };

  const HeaderAction = ({ students }: { students: RosterManagementStudentListItem[] }) => {
    if (null == action || RosterManagementStudentActionEnum.edit === action || 0 === students.length) {
      return null;
    }

    return (
      <Checkbox
        id={'select_all'}
        checked={students.length === selectedStudents.length}
        onChange={() => {
          setShowContinue(true);
          if (students.length === selectedStudents.length) {
            setSelectedStudents([]);

            return;
          }

          setSelectedStudents(students.map((student) => student.id));
        }}
      />
    );
  };

  const Action = ({ student }: { student: RosterManagementStudentListItem }) => {
    switch (action) {
      case RosterManagementStudentActionEnum.edit:
        return (
          <Edit onClick={() => onEdit(student)} />
        );
      case RosterManagementStudentActionEnum.moveToClass:
      case RosterManagementStudentActionEnum.addToExtraClass:
      case RosterManagementStudentActionEnum.removeFromClass:
      case RosterManagementStudentActionEnum.moveToYearGroup:
      case RosterManagementStudentActionEnum.activate:
      case RosterManagementStudentActionEnum.deactivate:
      case RosterManagementStudentActionEnum.archive:
      case RosterManagementStudentActionEnum.addTags:
      case RosterManagementStudentActionEnum.removeTags:
      case RosterManagementStudentActionEnum.setUsageCap:
      case RosterManagementStudentActionEnum.unarchive:
      case RosterManagementStudentActionEnum.completelyDeleteArchived:
        return (
          <Checkbox
            id={student.id}
            checked={selectedStudents.includes(student.id)}
            onChange={() => toggleSelectedStudent(student.id)}
          />
        );
      default:
        return null;
    }
  };

  const columns: Column<RosterManagementStudentListItem>[] = [
    // Action column. Without title
    {
      id: 'action',
      Header: () => (<HeaderAction students={students} />),
      accessor: 'id',
      Cell: ({ row }) => (<Action student={row.original} />),
      disableSortBy: true,
    },
    {
      id: 'name',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.STUDENTS.TABLE.STUDENT_NAME'),
      accessor: (original) => `${(original.lastName ?? '')}###${original.firstName ?? ''}`,
      Cell: ({ value } : { value: string }) => {
        const nameArray = value.split('###');

        return `${nameArray[1]} ${nameArray[0]}`;
      },
    },
    {
      id: 'active',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.STUDENTS.TABLE.ACTIVE'),
      accessor: 'active',
      Cell: ({ value }: { value: boolean }) => (<Active active={value} />),
      sortType: 'basic',
    },
    {
      id: 'year_group',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.STUDENTS.TABLE.YEAR_GROUP'),
      accessor: 'yearGroupId',
      Cell: ({ value }: { value: RosterManagementStudentListItem['yearGroupId'] }) => (<YearGroup yearGroupId={value} />),
    },
    {
      id: 'classes',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.STUDENTS.TABLE.CLASSES'),
      accessor: 'classes',
      Cell: ({ value }: { value: RosterManagementStudentListItem['classes'] }) => (<Groups groups={value} max={maxNumberOfClasses} />),
    },
    {
      id: 'tags',
      Header: translate<string>('pages.ROSTER_MANAGEMENT.STUDENTS.TABLE.TAGS'),
      accessor: 'tags',
      Cell: ({ value }: { value: RosterManagementStudentListItem['tags'] }) => (<Tags tags={value} max={maxNumberOfTags} />),
      sortType: 'basic',
    },
  ];

  useEffect(() => {
    setSelectedStudents([]);
  }, [ action, baseStudents.map((s) => s.id).join(',') ]);

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <>
      <GlobalStyle />
      <div className={'border-b-2 border-gray-300 mb-4'}></div>
      <div className={'mb-32'}>
        <Table
          id={'students'}
          cols={columns}
          content={students}
          customHeaderComponent={customHeaderComponent}
          initialState={{
            sortBy: [
              {
                id: 'name',
                desc: false,
              },
            ],
            pageSize: 25,
          }}
          disableSortRemove
        />
      </div>
      {(0 < selectedStudents.length && showContinue) && (
        <Continue
          onClick={() => onContinueWithSelected(baseStudents.filter((student) => selectedStudents.includes(student.id)))}
          isLoading={isContinueLoading}
          count={selectedStudents.length}
        />
      )}
    </>
  );
};

export default StudentsTable;
